// src/oura/lib/engagement.ts
//
// Reads the quotation's engagement.json into the seed the server takes.
// Shared by Start a plan and New client, so a quote reads the same in both.

import type { DeliverableSeed, EngagementSeed } from "@service/aeo/clientReview";

/**
 * What the reader builds. `covers` and `reported_by_hand` are deliberately NOT
 * set: the server authors them from the catalogue, and sending `[]` would tell
 * it this plan's lines cover nothing at all.
 *
 * `sourceAssessmentId` is the pitched scan the plan was sold on. The server
 * already takes it on the seed (BE `aeo_client_review.py:1924-1928`); the
 * reader used to list the key as known and then drop it (JOURNEY-SPEC §2.3).
 * Absent when the file does not carry one: the Client signed sheet then fills
 * in the business's latest usable scan, and never overwrites one given here.
 */
export type ParsedSeed = EngagementSeed & { sourceAssessmentId?: string | null };
type Seed = ParsedSeed;

/**
 * Top-level keys `EngagementSeedIn` accepts, snake and camel both, the server
 * takes either (`model_config = _CAMEL`, `populate_by_name`).
 *
 * Anything outside this set is NAMED rather than dropped. The only
 * engagement.json on disk today, Bee Choo Origin's, carries three keys the
 * reader had never heard of, and a silent drop is how a term nobody noticed was
 * missing ends up in a signed contract that the product does not implement.
 */
const KNOWN_KEYS = new Set([
    "ref", "client", "outlets", "deliverables", "deviations", "documents", "tracking",
    "plan_name", "planName", "term_months", "termMonths", "page_units", "pageUnits",
    "price_myr", "priceMyr", "outlet_addon_myr", "outletAddonMyr",
    "started_at", "startedAt", "source_assessment_id", "sourceAssessmentId",
]);

/** `snake_case` or `camelCase`, whichever the file used. */
function pick<T>(raw: Record<string, unknown>, snake: string, camel: string, fallback: T): T {
    const v = raw[camel] ?? raw[snake];
    return (v === undefined || v === null ? fallback : v) as T;
}

/**
 * The plan's outlet count. Only a real 0 is an online plan: null, a blank or an
 * unreadable value is unknown and reads as 1, never as 0 (Number("") is 0) (Jack, #4295).
 */
function outletCount(obj: Record<string, unknown>): number {
    const raw = pick<unknown>(obj, "outlets", "outlets", 1);
    const parsed = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() !== "" ? Number(raw) : Number.NaN;
    return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : 1;
}

export interface ParsedEngagement { seed: Seed; unknownKeys: string[] }

/** The pitched scan's id, when the file names one. Left off the seed otherwise, so "absent" stays absent. */
function sourceOf(obj: Record<string, unknown>): { sourceAssessmentId?: string } {
    const id = String(pick(obj, "source_assessment_id", "sourceAssessmentId", "")).trim();
    return id ? { sourceAssessmentId: id } : {};
}

/**
 * engagement.json -> the seed, plus the keys we could not account for.
 *
 * Throws with a sentence a staffer can act on. `ref` and `termMonths` are the
 * two the server refuses without, so they are refused here rather than sent to
 * be refused there.
 */
export function parseEngagement(text: string): ParsedEngagement {
    let raw: unknown;
    try {
        raw = JSON.parse(text);
    } catch {
        throw new Error("That is not valid JSON. Paste the file's contents, or choose the file itself.");
    }
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
        throw new Error("An engagement is a JSON object. This file holds something else.");
    }
    const obj = raw as Record<string, unknown>;
    const ref = String(pick(obj, "ref", "ref", "")).trim();
    if (!ref) throw new Error("No `ref` in the file. That is the quotation number, and the plan is filed under it.");
    const termMonths = Number(pick(obj, "term_months", "termMonths", 0));
    if (!Number.isFinite(termMonths) || termMonths < 1) {
        throw new Error("No usable `termMonths`. Every month on every deliverable is checked against the term, so it cannot be guessed.");
    }
    const rows = pick<unknown[]>(obj, "deliverables", "deliverables", []);
    const deliverables: DeliverableSeed[] = (Array.isArray(rows) ? rows : []).map((r) => {
        const d = (typeof r === "object" && r !== null ? r : {}) as Record<string, unknown>;
        const months = Array.isArray(d.months) ? d.months.map(Number).filter((n) => Number.isFinite(n)) : [];
        return {
            code: String(d.code ?? "").trim(),
            label: String(d.label ?? "").trim(),
            months,
            quantity: (d.quantity as string) ?? null,
            cadence: (d.cadence as string) ?? null,
            note: (d.note as string) ?? null,
        };
    });
    return {
        seed: {
            ref,
            client: String(pick(obj, "client", "client", "")),
            planName: String(pick(obj, "plan_name", "planName", "")),
            termMonths,
            pageUnits: Number(pick(obj, "page_units", "pageUnits", 0)) || 0,
            // 0 is an online plan (no outlets); only a missing or unreadable count reads as 1.
            outlets: outletCount(obj),
            startedAt: (pick<string | null>(obj, "started_at", "startedAt", null) || null),
            deliverables,
            deviations: (pick<string[]>(obj, "deviations", "deviations", []) as string[]) ?? [],
            documents: (pick<Array<{ title: string; file: string }>>(obj, "documents", "documents", []) as Array<{ title: string; file: string }>) ?? [],
            ...sourceOf(obj),
        },
        unknownKeys: Object.keys(obj).filter((k) => !KNOWN_KEYS.has(k)),
    };
}
